/**
 * quick-buy-popover-portal.js
 *
 * Fix: on the homepage (and any other place products render inside
 * `.product-list__inner--scroller`), the mobile Quick Buy popover
 * only uses the vertical space downward from the tapped card, making
 * the ATC button unreachable.
 *
 * Root cause: iOS Safari (WebKit) clips `position: fixed` descendants
 * of `overflow: hidden` ancestors to the ancestor's box. The theme
 * embeds `<quick-buy-popover>` inside each product card; when the card
 * is inside a horizontal scroller (`.product-list__inner--scroller`
 * has `overflow-y: hidden` on mobile), the popover paints inside that
 * clipped box instead of the viewport.
 *
 * Fix: teleport `<quick-buy-popover>` elements to `document.body` when
 * they open, restore them to their original parent when they close.
 * Portal pattern. Safe across all engines.
 *
 * Runtime-safe: no CSS changes, no Liquid changes. Pure JS enhancement
 * layered on top of the theme's own `<quick-buy-popover>` element.
 */
(function () {
  'use strict';

  // Marker so we can find the original slot on close.
  var PLACEHOLDER_ATTR = 'data-qbp-placeholder';

  function teleportToBody(popover) {
    if (popover.__qbpTeleported) return;
    var parent = popover.parentNode;
    if (!parent || parent === document.body) return;

    // Only teleport when the popover lives inside a scroller row.
    // (Preserves original behavior on collection pages / non-scroller contexts.)
    var scrollerAncestor = popover.closest('.product-list__inner--scroller');
    if (!scrollerAncestor) return;

    // Leave a marker in the original DOM position so we can restore.
    var placeholder = document.createComment('quick-buy-popover-portal');
    placeholder.__qbpFor = popover;
    popover.__qbpPlaceholder = placeholder;
    popover.__qbpOriginalParent = parent;
    parent.insertBefore(placeholder, popover);

    document.body.appendChild(popover);
    popover.__qbpTeleported = true;
  }

  function restoreFromBody(popover) {
    if (!popover.__qbpTeleported) return;
    var placeholder = popover.__qbpPlaceholder;
    var originalParent = popover.__qbpOriginalParent;
    if (placeholder && placeholder.parentNode) {
      placeholder.parentNode.insertBefore(popover, placeholder);
      placeholder.parentNode.removeChild(placeholder);
    } else if (originalParent && originalParent.isConnected) {
      // Fallback: original parent still exists but placeholder was
      // removed (shouldn't happen, but be safe).
      originalParent.appendChild(popover);
    }
    popover.__qbpPlaceholder = null;
    popover.__qbpOriginalParent = null;
    popover.__qbpTeleported = false;
  }

  function observePopover(popover) {
    if (popover.__qbpObserved) return;
    popover.__qbpObserved = true;

    // Handle already-open state (edge case: JS runs after popover opened).
    if (popover.hasAttribute('open')) {
      teleportToBody(popover);
    }

    var mo = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var m = mutations[i];
        if (m.type === 'attributes' && m.attributeName === 'open') {
          if (popover.hasAttribute('open')) {
            teleportToBody(popover);
          } else {
            // Small delay so the close transition can start against the
            // portal-mounted element before we move it back. 400ms is
            // longer than the theme's 250-600ms popover transition.
            setTimeout(function () {
              if (!popover.hasAttribute('open')) restoreFromBody(popover);
            }, 650);
          }
        }
      }
    });
    mo.observe(popover, { attributes: true, attributeFilter: ['open'] });
    popover.__qbpObserver = mo;
  }

  function scan(root) {
    var scope = root || document;
    var popovers = scope.querySelectorAll ? scope.querySelectorAll('quick-buy-popover') : [];
    popovers.forEach(observePopover);
  }

  // Initial scan.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { scan(); });
  } else {
    scan();
  }

  // Watch for dynamically added product cards (section rerenders, AJAX loads).
  var docObserver = new MutationObserver(function (mutations) {
    for (var i = 0; i < mutations.length; i++) {
      var m = mutations[i];
      for (var j = 0; j < m.addedNodes.length; j++) {
        var node = m.addedNodes[j];
        if (node.nodeType !== 1) continue;
        if (node.tagName && node.tagName.toLowerCase() === 'quick-buy-popover') {
          observePopover(node);
        } else if (node.querySelectorAll) {
          scan(node);
        }
      }
    }
  });
  docObserver.observe(document.body, { childList: true, subtree: true });
})();
